"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { SearchInput } from "./search";
import { MOBILE_SEARCH_TRANSITION_CLASS } from "./constants";

type MobileSearchToggleProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function MobileSearchToggle({
  open,
  onOpenChange,
}: MobileSearchToggleProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [clearSignal, setClearSignal] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isOpen = open ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (open === undefined) setUncontrolledOpen(next);
    },
    [onOpenChange, open]
  );

  const closeAndClear = useCallback(() => {
    setOpen(false);
    setClearSignal((v) => v + 1);
  }, [setOpen]);

  // Open from the button: commit synchronously, then focus the input within
  // the same tap. Committing `isOpen` makes the button's wrapper `inert`, and
  // Chrome (which focuses a tapped button) then drops focus to <body> — the
  // root's blur check would see focus outside and close the search ~10 ms
  // after it opened. flushSync is needed because the input's container is
  // `inert` until the commit, and focus() on it would do nothing. Focusing
  // inside the tap is also what lets iOS raise the keyboard.
  const handleOpenClick = () => {
    flushSync(() => setOpen(true));
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;
    // Fallback for an open that didn't come from the button (a controlled
    // `open` set by the parent). A no-op when handleOpenClick already
    // focused the input.
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) {
        closeAndClear();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeAndClear();
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleBlur = useCallback(() => {
    if (!isOpen) return;
    // Close when focus leaves the search area (e.g. user taps elsewhere).
    window.setTimeout(() => {
      const el = rootRef.current;
      if (!el) return;
      const active = document.activeElement;
      if (active && !el.contains(active)) {
        closeAndClear();
      }
    }, 0);
  }, [closeAndClear, isOpen]);

  return (
    <div
      ref={rootRef}
      className="sm:hidden flex items-center justify-end min-w-0 w-full"
      onBlur={handleBlur}
    >
      <div
        className={[
          `overflow-hidden transition-all ${MOBILE_SEARCH_TRANSITION_CLASS} ease-out`,
          isOpen
            ? "max-w-0 opacity-0 pointer-events-none"
            : "max-w-[44px] opacity-100",
        ].join(" ")}
        aria-hidden={isOpen}
        // `inert` removes focusable descendants from the tab order while
        // collapsed — `aria-hidden` alone with focusable children is an
        // a11y violation.
        inert={isOpen}
      >
        <button
          type="button"
          onClick={handleOpenClick}
          className="p-1.5 rounded-lg border border-[#2a475e]/70 bg-[#1b2838]/60 hover:bg-[#1f2e3f] hover:border-[#3a556e] transition-all flex-shrink-0 relative group focus:outline-none focus:ring-2 focus:ring-[#66c0f4]/30"
          aria-label="Open search"
          aria-expanded={false}
        >
          <svg
            className="w-4 h-4 text-[#c7d5e0] group-hover:text-[#66c0f4] transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <div className="absolute inset-0 rounded-lg bg-[#66c0f4]/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        </button>
      </div>

      <div
        className={[
          `min-w-0 ${
            isOpen ? "overflow-visible" : "overflow-hidden"
          } transition-all ${MOBILE_SEARCH_TRANSITION_CLASS} ease-out`,
          isOpen
            ? "max-w-[900px] w-full opacity-100 ml-2"
            : "max-w-0 opacity-0 ml-0 pointer-events-none",
        ].join(" ")}
        aria-hidden={!isOpen}
        inert={!isOpen}
      >
        <div
          className={[
            "flex items-center gap-2 min-w-0",
            `transition-all ${MOBILE_SEARCH_TRANSITION_CLASS} ease-out origin-right`,
            isOpen ? "translate-x-0 scale-100" : "translate-x-2 scale-95",
          ].join(" ")}
        >
          <div className="flex-1 min-w-0">
            <SearchInput
              inputRef={inputRef}
              clearSignal={clearSignal}
              onSelectResult={closeAndClear}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
